/**
 * Internal AEOS operations.
 *
 * This is an internal surface: AEOS integration health and control-plane configuration
 * for g/d/n/a operators. It is not a customer-facing product console.
 *
 * Estate and control-plane reads load together here so one refresh covers the whole
 * surface. The run feed and the change-request list own their own loading because both
 * are parameterised (status, cursor) and reload on their own terms; a refresh token
 * pulls them along with everything else.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import {
  Alert,
  Button,
  ContentLayout,
  Header,
  SpaceBetween,
  Tabs,
} from '@cloudscape-design/components';

import {
  getAeosControlPlane,
  getAeosEstate,
  type AeosControlPlaneSummary,
  type AeosEstateReport,
} from '../api/aeos';
import { AeosChangeView } from '../components/aeos/AeosChangeView';
import { AeosControlPlanePanel } from '../components/aeos/AeosControlPlanePanels';
import { AeosEconPanel } from '../components/aeos/AeosEconPanel';
import { AeosEstatePanel, AeosRunsPanel } from '../components/aeos/AeosEstatePanels';

export function AeosOps(): ReactElement {
  const [estate, setEstate] = useState<AeosEstateReport | undefined>();
  const [controlPlane, setControlPlane] = useState<AeosControlPlaneSummary | undefined>();
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [reloadToken, setReloadToken] = useState(0);

  const load = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(undefined);
    try {
      const [estateReport, controlPlaneSummary] = await Promise.all([
        getAeosEstate(),
        getAeosControlPlane(),
      ]);
      setEstate(estateReport);
      setControlPlane(controlPlaneSummary);
      setSelectedIntegrationId(
        (current) => current ?? estateReport.integrations[0]?.integrationId,
      );
    } catch (loadError) {
      setError(
        loadError instanceof Error ? loadError.message : 'Unable to read the AEOS estate',
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, reloadToken]);

  function refresh(): void {
    setReloadToken((current) => current + 1);
  }

  const invalidItemTotal =
    (estate?.invalidItemCount ?? 0) + (controlPlane?.invalidItemCount ?? 0);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Internal g/d/n/a operations: AEOS integration health and control-plane configuration, read cross-account, plus reviewed change intent. Not a customer-facing surface — the customer product console is ZTrust Control."
          actions={
            <Button iconName="refresh" loading={isLoading} onClick={refresh}>
              Refresh
            </Button>
          }
        >
          AEOS Internal Operations
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error ? (
          <Alert type="error" header="AEOS operations request failed">
            {error}
          </Alert>
        ) : null}
        {invalidItemTotal > 0 ? (
          <Alert
            type="warning"
            header={`${invalidItemTotal} AEOS record${invalidItemTotal === 1 ? '' : 's'} failed validation across this surface`}
          >
            Records that did not match the expected AEOS shape were dropped. Every count and
            timestamp below is therefore incomplete; the affected tab repeats this warning
            with its own count.
          </Alert>
        ) : null}
        <Tabs
          tabs={[
            {
              id: 'estate',
              label: 'Estate',
              content: (
                <AeosEstatePanel
                  isLoading={isLoading}
                  report={estate}
                  selectedIntegrationId={selectedIntegrationId}
                  onSelectIntegration={setSelectedIntegrationId}
                />
              ),
            },
            {
              id: 'runs',
              label: 'Runs',
              content: <AeosRunsPanel reloadToken={reloadToken} />,
            },
            {
              id: 'control-plane',
              label: 'Control plane',
              content: (
                <AeosControlPlanePanel isLoading={isLoading} summary={controlPlane} />
              ),
            },
            {
              id: 'econ',
              label: 'Unit economics',
              content: <AeosEconPanel reloadToken={reloadToken} />,
            },
            {
              id: 'changes',
              label: 'Change requests',
              content: <AeosChangeView reloadToken={reloadToken} />,
            },
          ]}
        />
      </SpaceBetween>
    </ContentLayout>
  );
}
